import {
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  ColaboradorPortal,
  EmpresaPortal,
  NotificacaoFamilyTarget,
  NotificacaoFamilyPayload,
  getColaboradoresNotif,
  getEmpresasNotif,
  sendNotificacaoFamily,
} from "src/apis/eezyfamily";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator, getStoredToken } from "src/utils/auth";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";

type FormState = {
  target: NotificacaoFamilyTarget;
  company_id: string;
  collaborator_id: string;
  subject: string;
  message: string;
};

const defaultValues: FormState = {
  target: "ALL_COLLABORATORS",
  company_id: "",
  collaborator_id: "",
  subject: "",
  message: "",
};

const EFNotificacoesPage: FC = (): JSX.Element => {
  const [form, setForm] = useState<FormState>(defaultValues);
  const [empresas, setEmpresas] = useState<EmpresaPortal[]>([]);
  const [colaboradores, setColaboradores] = useState<ColaboradorPortal[]>([]);
  const [collaboratorSearch, setCollaboratorSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchEmpresas = useCallback(async () => {
    try {
      const data = await getEmpresasNotif();
      setEmpresas(data);
    } catch (err: any) {
      setError(err?.message ?? "Erro ao carregar empresas.");
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  const fetchColaboradores = useCallback(
    async (companyId: string, name?: string) => {
      if (!companyId) {
        setColaboradores([]);
        return;
      }
      try {
        const data = await getColaboradoresNotif(Number(companyId), name);
        setColaboradores(data);
      } catch {
        setColaboradores([]);
      }
    },
    []
  );

  useEffect(() => {
    fetchColaboradores(form.company_id);
  }, [form.company_id, fetchColaboradores]);

  const debouncedCollaboratorSearch = useDebouncedCallback(
    (companyId: string, name: string) => {
      fetchColaboradores(companyId, name);
    },
    300
  );

  const optionalFields = useMemo(() => {
    if (form.target === "ALL_COLLABORATORS") {
      return ["company_id", "collaborator_id"];
    }
    if (form.target === "COMPANY_COLLABORATORS") {
      return ["collaborator_id"];
    }
    return [] as string[];
  }, [form.target]);

  const isSubmitDisabled = useMemo(
    () =>
      isSuccessOrAllRequiredFieldsAreFilled(success, form, optionalFields),
    [success, form, optionalFields]
  );

  const setField = (key: keyof FormState, value: string) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleTargetChange = (value: NotificacaoFamilyTarget) => {
    setForm((p) => ({
      ...p,
      target: value,
      company_id: value === "ALL_COLLABORATORS" ? "" : p.company_id,
      collaborator_id: value !== "ONE_COLLABORATOR" ? "" : p.collaborator_id,
    }));
    if (value !== "ONE_COLLABORATOR") {
      setCollaboratorSearch("");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload: NotificacaoFamilyPayload = {
        target: form.target,
        subject: form.subject,
        title: form.subject,
        message: form.message,
        token: getStoredToken(),
        ...(form.company_id && { company_id: Number(form.company_id) }),
        ...(form.collaborator_id && { collaborator_id: Number(form.collaborator_id) }),
      };
      await sendNotificacaoFamily(payload);
      setError("");
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message ?? "Erro ao enviar notificação.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(defaultValues);
    setCollaboratorSearch("");
    setColaboradores([]);
    setError("");
    setSuccess(false);
  };

  return (
    <PageContainer
      title="Notificações EezyFamily"
      error={error}
      guard={isUserAdministrator}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {/* Destino */}
        <FormGroup>
          <FormLabel>Destino</FormLabel>
          <Select
            value={form.target}
            onChange={(e) =>
              handleTargetChange(e.target.value as NotificacaoFamilyTarget)
            }
          >
            <MenuItem value="ALL_COLLABORATORS">
              Todos os colaboradores
            </MenuItem>
            <MenuItem value="COMPANY_COLLABORATORS">
              Colaboradores de uma empresa
            </MenuItem>
            <MenuItem value="ONE_COLLABORATOR">
              Colaborador específico
            </MenuItem>
          </Select>
          <FormHelperText>
            Quem deve receber esta notificação
          </FormHelperText>
        </FormGroup>

        {/* Empresa (condicional) */}
        {form.target !== "ALL_COLLABORATORS" && (
          <FormGroup>
            <FormLabel>Empresa</FormLabel>
            <Select
              value={form.company_id}
              onChange={(e) => {
                setField("company_id", e.target.value as string);
                setField("collaborator_id", "");
                setCollaboratorSearch("");
              }}
              displayEmpty
            >
              <MenuItem value="">Selecionar empresa...</MenuItem>
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.trade_name}
                </MenuItem>
              ))}
            </Select>
          </FormGroup>
        )}

        {/* Busca e seleção de colaborador (condicional) */}
        {form.target === "ONE_COLLABORATOR" && (
          <>
            <FormGroup>
              <FormLabel>Buscar Colaborador</FormLabel>
              <TextField
                value={collaboratorSearch}
                onChange={(e) => {
                  setCollaboratorSearch(e.target.value);
                  debouncedCollaboratorSearch(form.company_id, e.target.value);
                }}
                placeholder="Digitar nome do colaborador..."
                disabled={!form.company_id}
                helperText="Digite o nome para filtrar os colaboradores"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Colaborador</FormLabel>
              <Select
                value={form.collaborator_id}
                onChange={(e) =>
                  setField("collaborator_id", e.target.value as string)
                }
                displayEmpty
                disabled={!form.company_id}
              >
                <MenuItem value="">Selecionar colaborador...</MenuItem>
                {colaboradores.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormGroup>
          </>
        )}

        {/* Assunto */}
        <FormGroup>
          <FormLabel>Assunto</FormLabel>
          <TextField
            value={form.subject}
            onChange={(e) => setField("subject", e.target.value)}
            placeholder="Assunto da notificação"
          />
          <FormHelperText>Título que aparece na notificação</FormHelperText>
        </FormGroup>

        {/* Mensagem */}
        <FormGroup>
          <FormLabel>Mensagem</FormLabel>
          <TextField
            multiline
            minRows={4}
            value={form.message}
            onChange={(e) => setField("message", e.target.value)}
            placeholder="Conteúdo da mensagem..."
            helperText={`${form.message.length} de 1000 caracteres`}
            inputProps={{ maxLength: 1000 }}
          />
        </FormGroup>

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetForm}
          title="Enviar Notificação"
          loadingTitle="Enviando..."
        />
      </form>
    </PageContainer>
  );
};

export default EFNotificacoesPage;
